import {I18n} from '@aws-amplify/core';
import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';

import {StripeAmount} from '../../../components/StripeAmount';
import {StatusBadge} from '../profile/StatusBadge';
import {formatTimestamp} from '../../../utils/TableTimestamps';
import {PaymentIntent} from '../types';

// FNP-918: was `tables/columns.tsx` — a bare `getColumns()` array where EVERY
// column was `render`-only, with no `accessor` on any of them.
//
// That is the distinction that made this module unsortable. `render` controls
// how a cell LOOKS; `accessor` is what sorting sorts BY. A render-only config
// looks complete in review and makes sorting structurally impossible (§3z).
//
// ═══ AMOUNT SORTS AS A NUMBER IN MINOR UNITS. DO NOT "TIDY" THIS ═══
//
// `amount` is `Int!` in MINOR UNITS (cents) — 100 means $1.00. The cell RENDERS
// it as `amount / 100` via StripeAmount, but the accessor stays the raw NUMBER,
// so the comparator gets 900 vs 10000 and orders them correctly.
//
// Sorting the RENDERED value would be the bug: `(amount/100).toFixed(2)` is a
// STRING, and a string sorts lexically — "100.00" lands before "9.00", putting
// a $1,000 charge above a $9 one. That exact defect was found in `unitAmount`
// during this sweep. Keeping the accessor on the raw minor-unit number means
// display and sort agree while the ordering stays numeric.
//
// ═══ CREATED IS EPOCH SECONDS, NOT AN ISO STRING (FNP-1106) ═══
//
// `created: Int` is Stripe's epoch in SECONDS. `formatTimestamp` wraps moment,
// which reads a bare number as MILLISECONDS — so rendering `created` directly
// produces "Jan 20, 1970" on every row. That is live bug FNP-1106 in two other
// modules. The value is normalised to millis ONCE in usePaymentIntents
// (`createdAtMillis`) so the column can both render and sort the same value,
// and the accessor points at the normalised field rather than the raw one.
export const usePaymentIntentsTableColumns = () => {

    const columns: DataTableColumn<PaymentIntent>[] = [
        {
            // ⚠️ Sorts on the RAW MINOR-UNIT NUMBER (see the header comment).
            // The rendered string would sort lexically and mis-order by
            // magnitude, which on a money column is a wrong answer, not a
            // cosmetic one.
            accessor: 'amount',
            sortable: true,
            title: I18n.get('Amount'),
            width: '1fr',
            render: (item: PaymentIntent) => (
                <StripeAmount amount={item.amount} currency={item.currency}/>
            ),
        },
        {
            accessor: 'currency',
            sortable: true,
            title: I18n.get('Currency'),
            width: '1fr',
            render: (item: PaymentIntent) => item.currency ?? '—',
        },
        {
            accessor: 'customerId',
            sortable: true,
            title: I18n.get('Customer'),
            width: '1fr',
            // Nullable fields render the em-dash placeholder, never blank or
            // `undefined` (§13). `customerId` is `String` (nullable) — a
            // payment intent can exist before a customer is attached.
            render: (item: PaymentIntent) => item.customerId
                ? (
                    <NavLink to={`/customers/${item.customerId}`}>
                        {item.customerId}
                    </NavLink>
                )
                : '—',
        },
        {
            accessor: 'description',
            sortable: true,
            title: I18n.get('Description'),
            width: '1fr',
            // The old column rendered `item.description` bare, so a null
            // description rendered as nothing at all (§13).
            render: (item: PaymentIntent) => item.description ?? '—',
        },
        {
            accessor: 'status',
            sortable: true,
            title: I18n.get('Status'),
            width: '1fr',
            // Enum renders as a badge, not a raw string (§13).
            render: (item: PaymentIntent) => <StatusBadge status={item.status}/>,
        },
        {
            // Sorts on the normalised millisecond value so the ordering matches
            // what the cell displays. `created` (seconds) is still the source.
            accessor: 'createdAtMillis',
            sortable: true,
            title: I18n.get('Created'),
            width: '1fr',
            render: (item: PaymentIntent) => formatTimestamp(item.createdAtMillis),
        },
    ];

    return {columns};
};
